import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { REVEAL } from "@/lib/motion";
import { LeadsIcon, ReplyIcon, FollowUpIcon, CalendarIcon, ReportIcon } from "@/components/illustrations/LineIcons";

const STAGES = [
  {
    id: "find-leads",
    tab: "Find leads",
    title: "Find leads.",
    body: "Enriched lists of the right buyers, with verified email, phone and LinkedIn.",
    flow: ["Buyer profile agreed", "Contacts enriched", "List loaded"],
    icon: LeadsIcon,
    workflow: "/workflows/build-a-verified-list-of-the-right-buyers",
  },
  {
    id: "reply-instantly",
    tab: "Reply instantly",
    title: "Reply instantly.",
    body: "Every enquiry from your website, ads or WhatsApp answered in about a minute, day or night.",
    flow: ["Enquiry arrives", "Reply sent", "Lead qualified"],
    icon: ReplyIcon,
    workflow: "/workflows/reply-to-every-whatsapp-enquiry-in-60-seconds",
  },
  {
    id: "follow-up",
    tab: "Follow up",
    title: "Follow up.",
    body: "Sequences that keep going until the lead books or says no.",
    flow: ["Lead added", "Sequence runs", "Reply routed"],
    icon: FollowUpIcon,
    workflow: "/workflows/follow-up-a-lead-until-they-book-or-say-no",
  },
  {
    id: "book-meetings",
    tab: "Book meetings",
    title: "Book meetings.",
    body: "Qualified leads go straight into your calendar, with reminders.",
    flow: ["Slot offered", "Meeting booked", "Reminder sent"],
    icon: CalendarIcon,
    workflow: "/workflows/send-appointment-reminders-and-recover-no-shows",
  },
  {
    id: "report-pipeline",
    tab: "Report pipeline",
    title: "Report pipeline.",
    body: "A weekly report of leads, replies and meetings, sent to your inbox.",
    flow: ["Schedule runs", "Numbers summarised", "Report sent"],
    icon: ReportIcon,
    workflow: "/workflows/get-a-weekly-pipeline-report-without-opening-the-crm",
  },
];

const MiniFlow = ({ steps, stageId }) => (
  <div className="flex items-center gap-2" data-testid={`mini-flow-${stageId}`}>
    {steps.map((s, i) => (
      <React.Fragment key={s}>
        <span
          className="mini-pulse rounded-xl border border-[#1F1F1C]/20 px-3 py-2.5 text-xs font-semibold text-[#1F1F1C] leading-snug flex-1 text-center"
          style={{ animationDelay: `${i * 1.2}s` }}
        >
          {s}
        </span>
        {i < steps.length - 1 && <ArrowRight className="w-3.5 h-3.5 text-[#C8703A] shrink-0" strokeWidth={2.5} />}
      </React.Fragment>
    ))}
  </div>
);

export const HomeStages = () => {
  const { hash } = useLocation();
  const [active, setActive] = useState(0);

  useEffect(() => {
    const index = STAGES.findIndex((s) => `#${s.id}` === hash);
    if (index >= 0) setActive(index);
  }, [hash]);

  const stage = STAGES[active];
  const Icon = stage.icon;

  return (
    <section className="px-6 sm:px-8 pb-20 sm:pb-28" data-testid="home-stages">
      <div className="max-w-5xl mx-auto">
        <motion.h2
          {...REVEAL}
          className="font-fraunces font-extrabold text-3xl sm:text-4xl tracking-tight text-[#1F1F1C] max-w-2xl leading-tight"
        >
          Five places we automate your sales.
        </motion.h2>

        <div className="mt-10 flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" role="tablist" data-testid="stage-tabs">
          {STAGES.map((s, i) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              role="tab"
              aria-selected={i === active}
              onClick={() => setActive(i)}
              className={`btn-press shrink-0 rounded-2xl px-5 py-3 text-sm font-semibold border ${
                i === active
                  ? "bg-[#3D472D] text-[#F4E9D8] border-[#3D472D]"
                  : "border-[#1F1F1C]/20 text-[#4A463F] hover:border-[#C8703A] hover:text-[#C8703A]"
              }`}
              data-testid={`stage-tab-${s.id}`}
            >
              {s.tab}
            </a>
          ))}
        </div>

        <motion.div
          key={stage.id}
          id={stage.id}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 bg-[#EADCC6] rounded-2xl border border-[#1F1F1C]/10 p-7 sm:p-10 grid md:grid-cols-[1.1fr_1fr] gap-10 scroll-mt-28"
          data-testid={`stage-card-${stage.id}`}
        >
          <div>
            <Icon className="w-12 h-12 text-[#C8703A]" />
            <h3 className="mt-6 font-fraunces font-extrabold text-xl sm:text-2xl text-[#1F1F1C]">{stage.title}</h3>
            <p className="mt-4 text-sm md:text-base text-[#4A463F] leading-relaxed">{stage.body}</p>
            <Link
              to={stage.workflow}
              className="mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-[#C8703A] hover:gap-2.5 transition-all"
              data-testid={`stage-example-${stage.id}`}
            >
              See example <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
            </Link>
          </div>
          <div className="self-center">
            <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#4A463F] mb-4">How it runs</p>
            <MiniFlow steps={stage.flow} stageId={stage.id} />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HomeStages;
